import { clsx } from 'clsx'
import FormatNumericValue from '@components/shared/FormatNumericValue'
import {
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid'
import { useEffect, useMemo, useState } from 'react'
import Decimal from 'decimal.js'
import Tooltip from '@components/shared/Tooltip'
import { JupiterV6RouteInfo } from 'types/jupiter'
import { floorToDecimal } from 'utils/numbers'
import CircularProgressWithInterval from './CircularProgressWithInterval'
import SheenLoader from '@components/loading/SheenLoader'
import store from 'stores/store'
import { useCoingeckoPairUsdPrices } from 'hooks/useCoingeckoPairPrices'
import { useTranslation } from 'next-i18next'
import { ROUTER_INFO } from './useQuoteRoutes'
import { useLocalStorage } from 'hooks/useLocalStorageState'
import { REFERRER_KEY } from 'utils/constants'
import {
  freePairs,
  FULL_FEE_BPS,
  REBATED_FEE_BPS,
  ZERO_FEE_BPS,
} from 'utils/fee'

const EMPTY_COINGECKO_PRICES = {
  inputCoingeckoPrice: 0,
  outputCoingeckoPrice: 0,
}

const SwapInfo = ({
  selectedRoute,
  refetchRouteIntervalMs,
  refetchRoute,
  fetchingRoute,
  amountInAsDecimal,
  loading,
}: {
  selectedRoute: JupiterV6RouteInfo | null | undefined
  refetchRouteIntervalMs: number
  refetchRoute: (() => unknown) | undefined
  fetchingRoute: boolean
  amountInAsDecimal: Decimal
  loading: boolean
}) => {
  const { t } = useTranslation('swap')
  const inputToken = store((s) => s.swap.inputToken)
  const outputToken = store((s) => s.swap.outputToken)
  const [localReferrer] = useLocalStorage(REFERRER_KEY)

  const [coingeckoPrices, setCoingeckoPrices] = useState(EMPTY_COINGECKO_PRICES)
  const [swapRate, setSwapRate] = useState<boolean>(false)
  const { prices: fetchedCoingeckoPrices, loading: coingeckoPricesLoading } =
    useCoingeckoPairUsdPrices(
      inputToken?.coingecko_id,
      outputToken?.coingecko_id,
    )

  const priceImpactPct = useMemo(() => {
    try {
      if (!selectedRoute) return undefined
      return selectedRoute.origin === 'jupiter' && selectedRoute?.priceImpactPct
        ? selectedRoute?.priceImpactPct * 100
        : selectedRoute?.priceImpactPct
    } catch (e) {
      return undefined
    }
  }, [selectedRoute])

  const minimumReceived = useMemo(() => {
    if (!selectedRoute || !outputToken.decimals) return new Decimal(0)
    const decimals = outputToken.decimals
    const rawMinAmount = selectedRoute.otherAmountThreshold / 10 ** decimals
    return floorToDecimal(rawMinAmount, decimals)
  }, [selectedRoute, outputToken])

  const amountOutQuote = useMemo(() => {
    if (!selectedRoute?.outAmount || !outputToken.decimals) return
    return new Decimal(selectedRoute.outAmount).div(10 ** outputToken.decimals)
  }, [selectedRoute, outputToken])

  useEffect(() => {
    if (coingeckoPricesLoading) {
      setCoingeckoPrices(EMPTY_COINGECKO_PRICES)
    }
  }, [coingeckoPricesLoading])

  useEffect(() => {
    const inputId = inputToken.coingecko_id
    const outputId = outputToken.coingecko_id

    if (inputId && outputId && fetchedCoingeckoPrices) {
      setCoingeckoPrices({
        inputCoingeckoPrice: fetchedCoingeckoPrices[inputId],
        outputCoingeckoPrice: fetchedCoingeckoPrices[outputId],
      })
    }
  }, [inputToken, outputToken, fetchedCoingeckoPrices])

  const coinGeckoPriceDifference = useMemo(() => {
    if (
      !selectedRoute ||
      selectedRoute.error ||
      !coingeckoPrices.inputCoingeckoPrice ||
      !coingeckoPrices.outputCoingeckoPrice ||
      !amountOutQuote
    )
      return new Decimal(0)
    return amountInAsDecimal
      .div(amountOutQuote)
      .minus(
        new Decimal(coingeckoPrices.outputCoingeckoPrice).div(
          coingeckoPrices.inputCoingeckoPrice,
        ),
      )
      .div(amountInAsDecimal.div(amountOutQuote))
      .mul(100)
  }, [amountInAsDecimal, amountOutQuote, coingeckoPrices, selectedRoute])

  const feePct = feeBpsToPercentage(
    freePairs(selectedRoute?.inputMint, selectedRoute?.outputMint)
      ? ZERO_FEE_BPS
      : !localReferrer?.wallet
        ? FULL_FEE_BPS
        : REBATED_FEE_BPS,
  )

  return (
    <div
      className={clsx(
        'h-0 space-y-2.5 overflow-hidden transition-[height,margin] duration-200 ease-in',
        amountOutQuote != null && amountInAsDecimal.gt(0)
          ? 'mt-4 h-auto ease-in'
          : 'ease-out',
      )}
      style={{ interpolateSize: 'allow-keywords' }}
    >
      <>
        <div className="flex h-8 items-start justify-between text-xs">
          <p className="mr-4">{t('price')}</p>
          {loading ? (
            <SheenLoader>
              <div className="h-4 w-24 bg-th-bkg-2" />
            </SheenLoader>
          ) : (
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <div>
                  <div className="flex items-center justify-end">
                    <p className="text-right font-mono text-th-fgd-2">
                      {swapRate ? (
                        <>
                          1{' '}
                          <span className="font-body text-th-fgd-3">
                            {inputToken.symbol} ≈{' '}
                          </span>
                          <FormatNumericValue
                            value={
                              amountOutQuote != null && amountInAsDecimal.gt(0)
                                ? amountOutQuote.div(amountInAsDecimal)
                                : 0
                            }
                          />{' '}
                          <span className="font-body text-th-fgd-3">
                            {outputToken.symbol}
                          </span>
                        </>
                      ) : (
                        <>
                          1{' '}
                          <span className="font-body text-th-fgd-3">
                            {outputToken.symbol} ≈{' '}
                          </span>
                          <FormatNumericValue
                            value={
                              amountOutQuote != null && amountOutQuote.gt(0)
                                ? amountInAsDecimal.div(amountOutQuote)
                                : 0
                            }
                          />{' '}
                          <span className="font-body text-th-fgd-3">
                            {inputToken.symbol}
                          </span>
                        </>
                      )}
                    </p>
                    <ArrowsRightLeftIcon
                      className="ml-1 size-4 cursor-pointer text-th-fgd-2 hover:text-th-active"
                      onClick={() => setSwapRate(!swapRate)}
                    />
                  </div>
                  <div className="space-y-2 px-1 text-xs">
                    {inputToken.coingecko_id &&
                    outputToken.coingecko_id &&
                    coingeckoPrices?.outputCoingeckoPrice &&
                    coingeckoPrices?.inputCoingeckoPrice &&
                    coinGeckoPriceDifference.isFinite() ? (
                      <div
                        className={clsx(
                          'text-right font-mono',
                          coinGeckoPriceDifference.gt(1)
                            ? 'text-th-down'
                            : 'text-th-up',
                        )}
                      >
                        {coinGeckoPriceDifference.lte(0) ? '–' : '+'}
                        {Decimal.abs(coinGeckoPriceDifference).toFixed(1)}%{' '}
                        <span className="font-body">{t('vs-coingecko')}</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              <CircularProgressWithInterval
                refetchRouteIntervalMs={refetchRouteIntervalMs}
                refetchRoute={refetchRoute}
                fetchingRoute={fetchingRoute}
              />
            </div>
          )}
        </div>
        <div className="flex items-center justify-between text-xs">
          <p className="mr-4">{t('minimum-received')}</p>
          {loading ? (
            <SheenLoader>
              <div className="h-4 w-16 bg-th-bkg-2" />
            </SheenLoader>
          ) : (
            <p className="text-right font-mono text-th-fgd-2">
              {minimumReceived ? (
                <>
                  <FormatNumericValue value={minimumReceived.toNumber()} />{' '}
                  <span className="font-body text-th-fgd-3">
                    {outputToken.symbol}
                  </span>
                </>
              ) : (
                '–'
              )}
            </p>
          )}
        </div>
      </>
      <div className="flex justify-between text-xs">
        <Tooltip
          content={
            <>
              <p>{t('price-impact.desc')}</p>
              <p className="mt-1">{t('price-impact.warning')}</p>
            </>
          }
        >
          <p className="tooltip-underline mr-4">{t('price-impact.title')}</p>
        </Tooltip>
        {loading ? (
          <SheenLoader>
            <div className="h-4 w-10 bg-th-bkg-2" />
          </SheenLoader>
        ) : priceImpactPct ? (
          <span
            className={clsx(
              'flex items-center text-right font-mono',
              priceImpactPct < 0.2
                ? 'text-th-up'
                : priceImpactPct < 0.5
                  ? 'text-th-warning'
                  : 'text-th-down',
            )}
          >
            {priceImpactPct > 0.2 ? (
              <ExclamationTriangleIcon className="mr-1 mt-px size-3" />
            ) : null}
            <span>
              {priceImpactPct < 0.1
                ? '< 0.1%'
                : priceImpactPct
                  ? `${Number(priceImpactPct).toFixed(2)}%`
                  : t('price-impact.unavailable')}
            </span>
          </span>
        ) : (
          <span className="text-right font-mono">–</span>
        )}
      </div>
      <div className="flex justify-between text-xs text-th-fgd-4">
        <p>{t('best-route')}</p>{' '}
        {loading ? (
          <SheenLoader>
            <div className="h-4 w-28 bg-th-bkg-2" />
          </SheenLoader>
        ) : (
          <p>
            {routerName(selectedRoute?.origin, t('router-unknown'))}
            {/* show fee only on free swaps */}
            {feePct === 0 && (
              <>
                {' - '}
                <span className="text-th-success">
                  {t('platform-fee', { feePct })}
                </span>
              </>
            )}
          </p>
        )}
      </div>
    </div>
  )
}

export default SwapInfo

const routerName = (val: string | undefined, unknownTranslation: string) => {
  return val == null
    ? unknownTranslation
    : !(val in ROUTER_INFO)
      ? val
      : ROUTER_INFO[val as keyof typeof ROUTER_INFO].name
}

const feeBpsToPercentage = (feeBps: number) => {
  return feeBps / 100
}
